/**
 * TriageMemberPicker — CVG6-WEBTRIAGE (L138 + L142, 2026-07-13)
 *
 * Searchable, load-hinted member picker for the Lead Triage Dashboard.
 * Replaces the old team dropdown + team modal: type-ahead over the members of
 * ALL assignable teams (manager + executives per team, from
 * GET /leads/triage/assignable-members), each row rendered as
 * `«Name» · «team tag» · «N HOT ticking»`.
 *
 * pickAssignee contract (LOCKED): an executive row is sent as the existing
 * chosenUserId; the per-team "Team manager" row (role 'manager') sends NO
 * assignToUserId — the backend's manager fallback owns blank picks. The
 * mapping lives in triageHelpers.assignBodyForSelection.
 *
 * mode='reassign' (L142 — Reassign on a locked Assigned row) inserts a
 * confirm step before the write; mode='assign' picks directly.
 *
 * L238 (Round 6 Wave A, 2026-09-03) — a "Dealers" section under "People",
 * grouped by manager (GET /leads/triage/assignable-dealers), rendered ONLY
 * when `pickDealer` is given so every existing caller is unchanged. Each
 * managed group ends with "+ New dealer for «first name»" → an inline sheet
 * whose Create calls the parent's `createDealer` (POST /dealers) with THAT
 * group's managerUserId. The Unassigned group (managerUserId null) has no
 * "+ New dealer" — triage may not create an unmanaged dealer (server 400
 * MANAGER_REQUIRED). Assigning a dealer is in addition to the rep.
 */

import { useMemo, useState } from 'react';
import {
  dealerGroupLabel,
  dealerLoadHint,
  dealerQuickAddErrors,
  filterDealerGroups,
  filterMembers,
  insertDealerSorted,
  managerFirstName,
  memberLoadHint,
  type AssignableDealer,
  type AssignableDealerGroup,
  type AssignableMember,
  type DealerQuickAddInput,
} from './triageHelpers';

export interface CreateDealerInput {
  name: string;
  phone: string;
  pincode: string;
  gstin: string | null;
  managerUserId: string;
}

const UNASSIGNED_KEY = '__unassigned__';
const groupKey = (g: AssignableDealerGroup) => g.managerUserId ?? UNASSIGNED_KEY;

const EMPTY_FORM: DealerQuickAddInput = { name: '', phone: '', pincode: '', gstin: '' };

function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: unknown; error?: unknown } }; message?: unknown } | null;
  const fromBody = e?.response?.data?.message ?? e?.response?.data?.error;
  if (typeof fromBody === 'string' && fromBody) return fromBody;
  if (typeof e?.message === 'string' && e.message) return e.message;
  return 'Could not create the dealer.';
}

export function TriageMemberPicker({
  mode,
  leadName,
  members,
  loading,
  busy,
  onClose,
  onPick,
  dealerGroups,
  dealersLoading,
  pickDealer,
  createDealer,
}: {
  mode: 'assign' | 'reassign';
  leadName: string | null;
  /** null while the parent is still loading the roster. */
  members: AssignableMember[] | null;
  loading: boolean;
  busy: boolean;
  onClose: () => void;
  onPick: (member: AssignableMember) => void;
  /** L238 — null while loading; section renders only when `pickDealer` is given. */
  dealerGroups?: AssignableDealerGroup[] | null;
  dealersLoading?: boolean;
  pickDealer?: (dealer: AssignableDealer, group: AssignableDealerGroup) => void;
  /** L238 — parent posts POST /dealers; resolves with the new AssignableDealer. */
  createDealer?: (input: CreateDealerInput) => Promise<AssignableDealer>;
}) {
  const [query, setQuery] = useState('');
  const [confirmFor, setConfirmFor] = useState<AssignableMember | null>(null);

  // ── L238 state ──
  const [confirmDealer, setConfirmDealer] = useState<{ dealer: AssignableDealer; group: AssignableDealerGroup } | null>(null);
  const [addedDealers, setAddedDealers] = useState<Record<string, AssignableDealer[]>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean> | null>(null);
  const [sheetFor, setSheetFor] = useState<AssignableDealerGroup | null>(null);
  const [form, setForm] = useState<DealerQuickAddInput>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof DealerQuickAddInput, string>>>({});
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const showDealers = typeof pickDealer === 'function';

  const filtered = useMemo(
    () => filterMembers(members ?? [], query),
    [members, query],
  );

  /** Server groups + dealers created inline this session (sorted by name). */
  const effectiveGroups = useMemo<AssignableDealerGroup[]>(() => {
    if (!dealerGroups) return [];
    return dealerGroups.map((g) => {
      // Skip added dealers the server already lists — a parent refetch after an
      // inline create must not duplicate the row (I2).
      const known = new Set(g.dealers.map((d) => d.id));
      const extra = (addedDealers[groupKey(g)] ?? []).filter((d) => !known.has(d.id));
      if (!extra.length) return g;
      return { ...g, dealers: extra.reduce((acc, d) => insertDealerSorted(acc, d), g.dealers) };
    });
  }, [dealerGroups, addedDealers]);

  const filteredGroups = useMemo(
    () => filterDealerGroups(effectiveGroups, query),
    [effectiveGroups, query],
  );

  const searching = query.trim().length > 0;
  const isExpanded = (g: AssignableDealerGroup) => {
    if (searching) return true; // a match hidden behind a collapsed header is a missed dealer
    if (expanded && groupKey(g) in expanded) return expanded[groupKey(g)];
    return effectiveGroups[0] ? groupKey(g) === groupKey(effectiveGroups[0]) : false;
  };
  const toggleGroup = (g: AssignableDealerGroup) => {
    const k = groupKey(g);
    setExpanded((prev) => ({ ...(prev ?? {}), [k]: !isExpanded(g) }));
  };

  const pick = (m: AssignableMember) => {
    if (busy) return;
    if (mode === 'reassign' && confirmFor?.userId !== m.userId) {
      setConfirmDealer(null); // a people click disarms any dealer confirm (I1)
      setConfirmFor(m);
      return;
    }
    onPick(m);
  };

  const pickD = (d: AssignableDealer, g: AssignableDealerGroup) => {
    if (busy || !pickDealer) return;
    if (mode === 'reassign' && confirmDealer?.dealer.id !== d.id) {
      setConfirmFor(null);
      setConfirmDealer({ dealer: d, group: g });
      return;
    }
    pickDealer(d, g);
  };

  const openSheet = (g: AssignableDealerGroup) => {
    if (busy) return;
    setSheetFor(g);
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setSheetError(null);
  };
  const closeSheet = () => {
    if (creating) return;
    setSheetFor(null);
    setFieldErrors({});
    setSheetError(null);
  };

  const submitSheet = async () => {
    if (!sheetFor || !createDealer || creating) return;
    const managerUserId = sheetFor.managerUserId;
    if (!managerUserId) {
      setSheetError('A manager is required to add a dealer.');
      return;
    }
    const errs = dealerQuickAddErrors(form);
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;
    setSheetError(null);
    setCreating(true);
    try {
      const created = await createDealer({
        name: form.name.trim(),
        phone: form.phone.trim(),
        pincode: form.pincode.trim(),
        gstin: form.gstin.trim() || null,
        managerUserId,
      });
      const group = sheetFor;
      setAddedDealers((prev) => {
        const k = groupKey(group);
        return { ...prev, [k]: [...(prev[k] ?? []), created] };
      });
      setSheetFor(null);
      setCreating(false);
      pickDealer?.(created, group);
    } catch (err) {
      setSheetError(errorMessage(err));
      setCreating(false);
    }
  };

  const dealerConfirmCopy = (c: { dealer: AssignableDealer; group: AssignableDealerGroup }) => {
    const lead = leadName ? ` "${leadName}"` : '';
    const first = managerFirstName(c.group.managerName);
    return `Assign${lead} to dealer "${c.dealer.name}"${first ? ` (${first}'s)` : ''}?`;
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'reassign' ? 'Reassign lead' : 'Assign lead'}
          </h2>
          <button
            onClick={onClose}
            disabled={busy}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none disabled:opacity-50"
            aria-label="Close"
          >×</button>
        </div>
        {leadName && (
          <p className="text-sm text-gray-500 mb-3">
            Lead: <span className="font-medium text-gray-800">{leadName}</span>
          </p>
        )}

        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setConfirmFor(null); setConfirmDealer(null); }}
          placeholder={showDealers ? 'Search by name, team or dealer…' : 'Search by name or team…'}
          aria-label="Search members"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
        />

        <div className="max-h-[50vh] overflow-y-auto flex flex-col gap-1" role="listbox">
          {showDealers && (
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1 pt-1">People</div>
          )}
          {loading || members === null ? (
            <div className="text-sm text-gray-400 py-4 text-center">Loading members…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-gray-400 py-4 text-center">No members match.</div>
          ) : (
            filtered.map((m) => (
              <button
                key={`${m.teamId}:${m.userId}:${m.role}`}
                type="button"
                disabled={busy}
                onClick={() => pick(m)}
                className={`text-left px-3 py-2 rounded border text-sm disabled:opacity-50 transition-colors ${
                  confirmFor?.userId === m.userId && confirmFor?.teamId === m.teamId
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <span className="font-medium text-gray-900">
                  {m.role === 'manager' ? `${m.name} (Team manager)` : m.name}
                </span>
                <span className="text-gray-400"> · {m.teamName}</span>
                <span
                  className={`ml-1.5 inline-flex items-center text-xs font-medium rounded px-1.5 py-0.5 ${
                    m.openHotCount > 0 ? 'text-red-700 bg-red-50' : 'text-gray-500 bg-gray-100'
                  }`}
                  title="Assigned HOT leads with the SLA clock still ticking"
                >
                  {memberLoadHint(m.openHotCount)}
                </span>
              </button>
            ))
          )}

          {showDealers && (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1 pt-3">Dealers</div>
              {dealersLoading || !dealerGroups ? (
                <div className="text-sm text-gray-400 py-4 text-center">Loading dealers…</div>
              ) : filteredGroups.length === 0 ? (
                <div className="text-sm text-gray-400 py-4 text-center">
                  {searching ? 'No dealers match.' : 'No dealers yet.'}
                </div>
              ) : (
                filteredGroups.map((g) => {
                  const k = groupKey(g);
                  const open = isExpanded(g);
                  const managed = Boolean(g.managerUserId);
                  return (
                    <div key={k} className="flex flex-col gap-1">
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => toggleGroup(g)}
                        className="text-left px-2 py-1.5 text-sm font-medium text-gray-800 rounded hover:bg-gray-50 flex items-center justify-between"
                      >
                        <span>{dealerGroupLabel(g)}</span>
                        <span aria-hidden="true" className="text-gray-400 text-xs">{open ? '▾' : '▸'}</span>
                      </button>
                      {open && (
                        <>
                          {g.dealers.map((d) => {
                            const armed = confirmDealer?.dealer.id === d.id;
                            return (
                              <button
                                key={d.id}
                                type="button"
                                role="option"
                                aria-selected={armed}
                                disabled={busy}
                                onClick={() => pickD(d, g)}
                                className={`text-left px-3 py-2 ml-2 rounded border text-sm disabled:opacity-50 transition-colors ${
                                  armed
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                                }`}
                              >
                                <span className="font-medium text-gray-900">{d.name}</span>
                                {d.city && <span className="text-gray-400"> · {d.city}</span>}
                                <span className="text-gray-400"> · </span>
                                <span
                                  className={`inline-flex items-center text-xs font-medium rounded px-1.5 py-0.5 ${
                                    d.openLeads > 0 ? 'text-amber-700 bg-amber-50' : 'text-gray-500 bg-gray-100'
                                  }`}
                                  title="Open leads currently routed to this dealer"
                                >
                                  {dealerLoadHint(d.openLeads)}
                                </span>
                                <span className="ml-1.5 inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 text-indigo-700 bg-indigo-50">
                                  Dealer
                                </span>
                              </button>
                            );
                          })}
                          {managed && createDealer && (
                            <button
                              type="button"
                              disabled={busy || creating}
                              onClick={() => openSheet(g)}
                              className="text-left px-3 py-1.5 ml-2 text-sm text-blue-700 hover:underline disabled:opacity-50"
                            >
                              {`+ New dealer for ${managerFirstName(g.managerName)}`}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })
              )}
              <p className="text-xs text-gray-500 px-1 pt-2">Assigning a dealer is in addition to the rep.</p>
            </>
          )}
        </div>

        {showDealers && sheetFor && (
          <div className="mt-4 p-3 rounded border border-gray-300 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-gray-900">New dealer</div>
              <div className="text-xs text-gray-600">Manager: {sheetFor.managerName}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {([
                ['name', 'Name', 'text', 'col-span-2'],
                ['phone', 'Phone', 'tel', ''],
                ['pincode', 'Pincode', 'text', ''],
                ['gstin', 'GSTIN (optional)', 'text', 'col-span-2'],
              ] as Array<[keyof DealerQuickAddInput, string, string, string]>).map(([field, label, type, span]) => (
                <label key={field} className={`flex flex-col gap-0.5 text-xs text-gray-700 ${span}`}>
                  {label}
                  <input
                    type={type}
                    value={form[field]}
                    disabled={creating}
                    onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                    className={`px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
                      fieldErrors[field] ? 'border-red-400' : 'border-gray-300'
                    }`}
                  />
                  {fieldErrors[field] && <span className="text-red-600">{fieldErrors[field]}</span>}
                </label>
              ))}
            </div>
            {sheetError && <div className="mt-2 text-xs text-red-600">{sheetError}</div>}
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeSheet}
                disabled={creating}
                className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >Cancel</button>
              <button
                type="button"
                onClick={() => { void submitSheet(); }}
                disabled={creating || busy}
                className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
              >{creating ? 'Creating…' : 'Create'}</button>
            </div>
          </div>
        )}

        {mode === 'reassign' && confirmFor && (
          <div className="mt-4 p-3 rounded border border-blue-300 bg-blue-50">
            <div className="text-sm text-gray-800">
              Reassign{leadName ? ` "${leadName}"` : ''} to{' '}
              <span className="font-semibold">
                {confirmFor.role === 'manager' ? `${confirmFor.name} (Team manager)` : confirmFor.name}
              </span>{' '}
              · {confirmFor.teamName}?
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmFor(null)}
                disabled={busy}
                className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >Back</button>
              <button
                type="button"
                onClick={() => onPick(confirmFor)}
                disabled={busy}
                className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
              >{busy ? 'Reassigning…' : 'Confirm reassign'}</button>
            </div>
          </div>
        )}

        {mode === 'reassign' && showDealers && confirmDealer && !confirmFor && (
          <div className="mt-4 p-3 rounded border border-blue-300 bg-blue-50">
            <div className="text-sm text-gray-800">{dealerConfirmCopy(confirmDealer)}</div>
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDealer(null)}
                disabled={busy}
                className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >Back</button>
              <button
                type="button"
                onClick={() => pickDealer?.(confirmDealer.dealer, confirmDealer.group)}
                disabled={busy}
                className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
              >{busy ? 'Reassigning…' : 'Confirm reassign'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
